import React from 'react';
import {
  Bell,
  Hand,
  MessageCircle,
  MessageSquareText,
  Phone,
  PhoneCall,
  PhoneForwarded,
  RotateCcw,
  ShieldAlert,
  Undo2,
  type LucideIcon,
} from 'lucide-react';
import type { NotificationKind, StaffNotification } from '@/services/notificationService';
import { isUnread, notificationExternalUrl } from '@/lib/notificationList';
import { isPhoneCallbackNotification } from '@/lib/callbackTicket';
import { NotificationRow, PanelSectionTitle } from './NotificationRow';
import { dayGroup, formatRelative, notificationDetail, type DayGroup } from './notificationFormat';

const KIND_ICONS: Record<NotificationKind, LucideIcon> = {
  handoff_requested: Hand,
  customer_waiting: MessageCircle,
  handoff_unanswered: RotateCcw,
  callback_requested: Undo2,
  callback_window_closing: Undo2,
  whatsapp_activity: MessageSquareText,
  call_live: Phone,
  call_transfer: PhoneForwarded,
  whatsapp_channel_blocked: ShieldAlert,
};

/** A callback wish from a phone call gets the phone, not the WhatsApp „Rückmeldung“ arrow. */
function notificationIcon(item: StaffNotification): LucideIcon {
  if (isPhoneCallbackNotification(item)) return PhoneCall;
  return KIND_ICONS[item.kind] ?? Bell;
}

const GROUP_ORDER: DayGroup[] = ['Heute', 'Gestern', 'Älter'];

interface NotificationListProps {
  items: StaffNotification[];
  now: number;
  unreadCount: number;
  onOpen: (item: StaffNotification) => void;
  onMarkAllRead: () => void;
  showAgentNumber: boolean;
}


/** „Benachrichtigungen“: grouped by day, unread emphasised, „Alle gelesen“ in the header. */
export function NotificationList({ items, now, unreadCount, onOpen, onMarkAllRead, showAgentNumber }: NotificationListProps) {
  if (items.length === 0) return null;
  const groups = GROUP_ORDER.map((group) => ({
    group,
    entries: items.filter((item) => dayGroup(item.updated_at, now) === group),
  })).filter((g) => g.entries.length > 0);

  return (
    <section aria-labelledby="bell-notifications-title">
      <PanelSectionTitle
        id="bell-notifications-title"
        action={
          <button
            type="button"
            // aria-disabled, not disabled: after the click the button stays focusable.
            onClick={() => {
              if (unreadCount > 0) onMarkAllRead();
            }}
            aria-disabled={unreadCount === 0}
            className="rounded-md px-1.5 py-0.5 text-xs font-semibold text-brand transition-colors hover:bg-brand-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 aria-disabled:cursor-default aria-disabled:text-muted-foreground/60 aria-disabled:hover:bg-transparent"
          >
            Alle gelesen
          </button>
        }
      >
        Benachrichtigungen
      </PanelSectionTitle>
      {groups.map(({ group, entries }) => (
        <div key={group}>
          {groups.length > 1 && (
            <p className="px-2.5 pb-0.5 pt-1.5 text-[11px] font-medium text-muted-foreground/80">{group}</p>
          )}
          <ul className="space-y-0.5">
            {entries.map((item) => {
              const unread = isUnread(item);
              const urgentOpen = item.urgent && unread;
              const externalUrl = notificationExternalUrl(item);
              return (
                <NotificationRow
                  key={item.id}
                  icon={notificationIcon(item)}
                  urgent={urgentOpen}
                  danger={item.kind === 'whatsapp_channel_blocked'}
                  href={externalUrl ?? undefined}
                  unread={unread}
                  muted={!unread}
                  title={
                    <>
                      {unread && <span className="sr-only">Ungelesen: </span>}
                      {item.title}
                      {externalUrl && <span className="sr-only"> – öffnet Meta in einem neuen Tab</span>}
                    </>
                  }
                  detail={notificationDetail(item, showAgentNumber)}
                  meta={formatRelative(item.updated_at, now)}
                  onClick={() => onOpen(item)}
                />
              );
            })}
          </ul>
        </div>
      ))}
    </section>
  );
}

export default NotificationList;
